import { useEffect, useMemo, useState } from 'react';
import { useMutation } from 'urql';
import PageHeader from '../../../components/PageHeader.jsx';
import StickyActions from '../../../components/StickyActions.jsx';
import { Field } from '../../../components/Field.jsx';
import { CREATE_PROVEEDOR, UPDATE_PROVEEDOR } from './gql.js';

const condicionIVAOptions = [
  { value: 'RI', label: 'Responsable Inscripto' },
  { value: 'MT', label: 'Monotributo' },
  { value: 'EX', label: 'Exento' },
  { value: 'CF', label: 'Consumidor Final' },
  { value: 'RNI', label: 'No inscripto' },
];

const initialState = {
  razonSocial: '',
  cuit: '',
  condicionIVA: 'RI',
  email: '',
  telefono: '',
  direccion: '',
  localidad: '',
  provincia: '',
  notas: '',
  activo: true,
  fechaAlta: '',
};

const inputClassName =
  'w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.92)] px-4 py-3 text-sm text-[rgb(var(--fg))] shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]';

const panelClassName =
  'rounded-[28px] border border-[rgba(var(--border),0.85)] bg-[linear-gradient(180deg,rgba(var(--panel),0.95),rgba(var(--panel),0.78))] p-6 shadow-[0_18px_50px_rgba(15,23,42,0.08)]';

function formatCuit(value) {
  const digits = String(value || '')
    .replace(/[^\d]/g, '')
    .slice(0, 11);

  if (digits.length <= 2) return digits;
  if (digits.length <= 10) return `${digits.slice(0, 2)}-${digits.slice(2, 10)}`;
  return `${digits.slice(0, 2)}-${digits.slice(2, 10)}-${digits.slice(10)}`;
}

function getCuitDigits(value) {
  return String(value || '').replace(/[^\d]/g, '');
}

function getFormErrorMessage(error, isEdit) {
  if (!error) {
    return isEdit ? 'No pudimos guardar los cambios del proveedor.' : 'No pudimos crear el proveedor.';
  }

  const graphQLError = Array.isArray(error.graphQLErrors) ? error.graphQLErrors[0] : null;
  const rawMessage =
    graphQLError?.extensions?.detail ||
    graphQLError?.message ||
    error.networkError?.message ||
    error.message ||
    '';

  const normalizedMessage = String(rawMessage).toLowerCase();

  if (
    normalizedMessage.includes('tenant no encontrado') ||
    normalizedMessage.includes('context creation failed') ||
    normalizedMessage.includes('no se pudo conectar a la base de datos del tenant')
  ) {
    return 'No pudimos conectar este proveedor con la empresa actual. Revisa el tenant activo y volve a intentar.';
  }

  if (normalizedMessage.includes('duplicate') || normalizedMessage.includes('duplicado')) {
    return 'Ya existe un proveedor con ese CUIT o razon social.';
  }

  return rawMessage || (isEdit ? 'No pudimos guardar los cambios del proveedor.' : 'No pudimos crear el proveedor.');
}

function validateFormState(formState) {
  const nextErrors = {};

  if (!formState.razonSocial.trim()) {
    nextErrors.razonSocial = 'La razon social es obligatoria.';
  } else if (formState.razonSocial.trim().length < 3) {
    nextErrors.razonSocial = 'Ingresa al menos 3 caracteres.';
  }

  const cuitDigits = getCuitDigits(formState.cuit);
  if (!cuitDigits) {
    nextErrors.cuit = 'El CUIT es obligatorio.';
  } else if (cuitDigits.length !== 11) {
    nextErrors.cuit = 'Ingresa un CUIT valido de 11 digitos.';
  }

  if (formState.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formState.email.trim())) {
    nextErrors.email = 'Ingresa un email valido.';
  }

  return nextErrors;
}

function InfoPill({ label, value, tone = 'default' }) {
  const toneClassName =
    tone === 'success'
      ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200'
      : tone === 'warning'
        ? 'border-amber-500/30 bg-amber-500/10 text-amber-200'
        : 'border-[rgba(var(--border),0.85)] bg-[rgba(var(--subpanel),0.6)] text-[rgb(var(--muted))]';

  return (
    <div className={`rounded-2xl border px-4 py-3 ${toneClassName}`}>
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em]">{label}</p>
      <p className="mt-1 text-sm font-medium text-[rgb(var(--fg))]">{value}</p>
    </div>
  );
}

function SectionHeader({ eyebrow, title, description }) {
  return (
    <div className="flex flex-col gap-1">
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[rgb(var(--muted))]">{eyebrow}</p>
      <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">{title}</h2>
      <p className="text-sm text-muted">{description}</p>
    </div>
  );
}

export default function ProveedorForm({ initialData, onCancel, onSaved }) {
  const [formState, setFormState] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEdit = Boolean(initialData?.id);
  const [, createProveedor] = useMutation(CREATE_PROVEEDOR);
  const [, updateProveedor] = useMutation(UPDATE_PROVEEDOR);

  useEffect(() => {
    if (initialData) {
      setFormState({
        razonSocial: initialData.razonSocial ?? '',
        cuit: formatCuit(initialData.cuit ?? ''),
        condicionIVA: initialData.condicionIVA ?? 'RI',
        email: initialData.email ?? '',
        telefono: initialData.telefono ?? '',
        direccion: initialData.direccion ?? '',
        localidad: initialData.localidad ?? '',
        provincia: initialData.provincia ?? '',
        notas: initialData.notas ?? '',
        activo: Boolean(initialData.activo ?? true),
        fechaAlta: initialData.fechaAlta ?? '',
      });
    } else {
      setFormState(initialState);
    }
    setErrors({});
    setIsSubmitting(false);
  }, [initialData]);

  const condicionOptions = useMemo(() => condicionIVAOptions, []);
  const cuitDigits = useMemo(() => getCuitDigits(formState.cuit), [formState.cuit]);
  const completionCount = useMemo(() => {
    const trackedValues = [
      formState.razonSocial.trim(),
      cuitDigits.length === 11 ? cuitDigits : '',
      formState.condicionIVA,
      formState.email.trim(),
      formState.telefono.trim(),
      formState.direccion.trim(),
      formState.localidad.trim(),
      formState.provincia.trim(),
    ];

    return trackedValues.filter(Boolean).length;
  }, [cuitDigits, formState]);

  const handleChange = event => {
    const { name, value, type, checked } = event.target;
    const nextValue =
      type === 'checkbox'
        ? checked
        : name === 'cuit'
          ? formatCuit(value)
          : value;

    setFormState(prev => ({
      ...prev,
      [name]: nextValue,
    }));

    setErrors(prev => {
      if (!prev[name] && !prev.form) {
        return prev;
      }

      const nextErrors = { ...prev };
      delete nextErrors.form;
      if (nextErrors[name]) {
        const validated = validateFormState({ ...formState, [name]: nextValue });
        if (!validated[name]) {
          delete nextErrors[name];
        }
      }
      return nextErrors;
    });
  };

  const handleSubmit = async event => {
    event.preventDefault();

    const nextErrors = validateFormState(formState);
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    const input = {
      razonSocial: formState.razonSocial.trim(),
      cuit: formatCuit(formState.cuit),
      condicionIVA: formState.condicionIVA,
      email: formState.email.trim() || null,
      telefono: formState.telefono.trim() || null,
      direccion: formState.direccion.trim() || null,
      localidad: formState.localidad.trim() || null,
      provincia: formState.provincia.trim() || null,
      notas: formState.notas.trim() || null,
      activo: Boolean(formState.activo),
      fechaAlta: formState.fechaAlta || null,
    };

    try {
      const result = isEdit
        ? await updateProveedor({ id: initialData.id, input })
        : await createProveedor({ input });

      if (result.error) {
        setErrors({ form: getFormErrorMessage(result.error, isEdit) });
        setIsSubmitting(false);
        return;
      }

      onSaved?.();
    } catch (error) {
      setErrors({ form: getFormErrorMessage(error, isEdit) });
      setIsSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="mx-auto flex w-full max-w-[1400px] flex-col gap-6 px-4 py-6 md:px-6 md:py-8"
      noValidate
    >
      <PageHeader
        title={isEdit ? 'Editar proveedor' : 'Nuevo proveedor'}
        subtitle="Carga los datos fiscales y de contacto en una sola vista, con validaciones claras y menos friccion al completar."
      />

      <section className="grid gap-4 lg:grid-cols-[minmax(0,1.8fr)_minmax(280px,0.95fr)]">
        <div className={`${panelClassName} relative overflow-hidden`}>
          <div className="absolute inset-x-0 top-0 h-1 bg-[linear-gradient(90deg,rgba(var(--brand),0.85),rgba(56,189,248,0.7),rgba(16,185,129,0.7))]" />
          <div className="grid gap-4 md:grid-cols-3">
            <InfoPill
              label="Estado"
              value={formState.activo ? 'Proveedor activo' : 'Proveedor inactivo'}
              tone={formState.activo ? 'success' : 'warning'}
            />
            <InfoPill label="CUIT" value={formState.cuit || 'Pendiente'} />
            <InfoPill label="Completado" value={`${completionCount}/8 datos clave`} />
          </div>
          <div className="mt-5 rounded-2xl border border-[rgba(var(--border),0.8)] bg-[rgba(var(--subpanel),0.45)] px-4 py-3 text-sm text-[rgb(var(--muted))]">
            Usa TAB para avanzar rapido. El CUIT se formatea automaticamente y se guarda con guiones.
          </div>
        </div>

        <aside className={`${panelClassName} flex flex-col justify-between gap-4`}>
          <SectionHeader
            eyebrow="Resumen"
            title="Checklist de alta"
            description="Los datos minimos quedan visibles sin tener que recorrer todo el formulario."
          />
          <div className="grid gap-3 text-sm text-[rgb(var(--fg))]">
            <div className="rounded-2xl border border-[rgba(var(--border),0.75)] bg-[rgba(var(--bg),0.55)] px-4 py-3">
              <p className="font-medium">Fiscal</p>
              <p className="mt-1 text-muted">Razon social, CUIT y condicion IVA.</p>
            </div>
            <div className="rounded-2xl border border-[rgba(var(--border),0.75)] bg-[rgba(var(--bg),0.55)] px-4 py-3">
              <p className="font-medium">Contacto</p>
              <p className="mt-1 text-muted">Email y telefono para seguimiento de compras.</p>
            </div>
            <div className="rounded-2xl border border-[rgba(var(--border),0.75)] bg-[rgba(var(--bg),0.55)] px-4 py-3">
              <p className="font-medium">Ubicacion</p>
              <p className="mt-1 text-muted">Direccion, localidad y provincia para referencia operativa.</p>
            </div>
          </div>
        </aside>
      </section>

      {errors.form ? (
        <div className="rounded-2xl border border-red-500/35 bg-red-500/10 px-4 py-3 text-sm text-red-100">
          <p className="font-semibold">No pudimos guardar el proveedor.</p>
          <p className="mt-1 text-red-100/90">{errors.form}</p>
        </div>
      ) : null}

      <section className={panelClassName}>
        <SectionHeader
          eyebrow="1. Datos fiscales"
          title="Identificacion principal"
          description="Completa primero lo imprescindible para dar de alta el proveedor."
        />
        <div className="mt-5 grid grid-cols-12 gap-4">
          <Field label="Razon social" hint={errors.razonSocial} className="col-span-12 lg:col-span-6">
            <input
              name="razonSocial"
              value={formState.razonSocial}
              onChange={handleChange}
              placeholder="Ej: Transporte del Centro SRL"
              className={inputClassName}
            />
          </Field>
          <Field label="CUIT" hint={errors.cuit} className="col-span-12 md:col-span-6 lg:col-span-3">
            <input
              name="cuit"
              value={formState.cuit}
              onChange={handleChange}
              inputMode="numeric"
              autoComplete="off"
              placeholder="##-########-#"
              className={inputClassName}
            />
          </Field>
          <Field label="Condicion IVA" className="col-span-12 md:col-span-6 lg:col-span-3">
            <select
              name="condicionIVA"
              value={formState.condicionIVA}
              onChange={handleChange}
              className={inputClassName}
            >
              {condicionOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </Field>
          <div className="col-span-12 rounded-2xl border border-[rgba(var(--border),0.8)] bg-[rgba(var(--subpanel),0.38)] px-4 py-3 text-sm text-[rgb(var(--muted))]">
            {cuitDigits.length === 0
              ? 'El formato esperado es 2-8-1 digitos.'
              : cuitDigits.length < 11
                ? `Faltan ${11 - cuitDigits.length} digitos para completar el CUIT.`
                : 'CUIT completo y listo para guardar.'}
          </div>
        </div>
      </section>

      <section className={panelClassName}>
        <SectionHeader
          eyebrow="2. Contacto y ubicacion"
          title="Datos operativos"
          description="Agrupados para evitar scroll innecesario y facilitar la carga."
        />
        <div className="mt-5 grid grid-cols-12 gap-4">
          <Field label="Email" hint={errors.email} className="col-span-12 md:col-span-6 lg:col-span-4">
            <input
              name="email"
              type="email"
              value={formState.email}
              onChange={handleChange}
              placeholder="compras@proveedor.com"
              className={inputClassName}
            />
          </Field>
          <Field label="Telefono" className="col-span-12 md:col-span-6 lg:col-span-4">
            <input
              name="telefono"
              value={formState.telefono}
              onChange={handleChange}
              placeholder="+54 11 5555 5555"
              className={inputClassName}
            />
          </Field>
          <Field label="Fecha de alta" className="col-span-12 md:col-span-6 lg:col-span-4">
            <input
              type="date"
              name="fechaAlta"
              value={formState.fechaAlta ?? ''}
              onChange={handleChange}
              className={inputClassName}
            />
          </Field>
          <Field label="Direccion" className="col-span-12 lg:col-span-6">
            <input
              name="direccion"
              value={formState.direccion}
              onChange={handleChange}
              placeholder="Calle, numero y referencia"
              className={inputClassName}
            />
          </Field>
          <Field label="Localidad" className="col-span-12 md:col-span-6 lg:col-span-3">
            <input
              name="localidad"
              value={formState.localidad}
              onChange={handleChange}
              placeholder="Localidad"
              className={inputClassName}
            />
          </Field>
          <Field label="Provincia" className="col-span-12 md:col-span-6 lg:col-span-3">
            <input
              name="provincia"
              value={formState.provincia}
              onChange={handleChange}
              placeholder="Provincia"
              className={inputClassName}
            />
          </Field>
        </div>
      </section>

      <section className={panelClassName}>
        <SectionHeader
          eyebrow="3. Estado interno"
          title="Seguimiento"
          description="Notas y estado visible en un bloque corto al final."
        />
        <div className="mt-5 grid grid-cols-12 gap-4">
          <Field label="Notas" className="col-span-12 lg:col-span-8">
            <textarea
              name="notas"
              value={formState.notas}
              onChange={handleChange}
              rows={5}
              placeholder="Observaciones internas, condiciones de compra o acuerdos especiales."
              className={inputClassName}
            />
          </Field>
          <div className="col-span-12 lg:col-span-4">
            <div className="h-full rounded-2xl border border-[rgba(var(--border),0.8)] bg-[rgba(var(--subpanel),0.38)] p-5">
              <p className="text-sm font-medium text-[rgb(var(--fg))]">Estado del proveedor</p>
              <p className="mt-1 text-sm text-muted">
                Controla si aparece como disponible para nuevas compras.
              </p>
              <button
                type="button"
                role="switch"
                aria-checked={formState.activo}
                onClick={() => setFormState(prev => ({ ...prev, activo: !prev.activo }))}
                className={`mt-5 inline-flex w-full items-center justify-between rounded-2xl border px-4 py-3 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] ${
                  formState.activo
                    ? 'border-emerald-500/35 bg-emerald-500/10'
                    : 'border-amber-500/35 bg-amber-500/10'
                }`}
              >
                <span>
                  <span className="block text-sm font-semibold text-[rgb(var(--fg))]">
                    {formState.activo ? 'Activo' : 'Inactivo'}
                  </span>
                  <span className="mt-1 block text-xs text-muted">
                    {formState.activo ? 'Disponible para operar.' : 'Oculto para nuevas operaciones.'}
                  </span>
                </span>
                <span
                  className={`inline-flex h-7 min-w-14 items-center justify-center rounded-full px-3 text-xs font-semibold ${
                    formState.activo ? 'bg-emerald-500/20 text-emerald-100' : 'bg-amber-500/20 text-amber-100'
                  }`}
                >
                  {formState.activo ? 'SI' : 'NO'}
                </span>
              </button>
            </div>
          </div>
        </div>
      </section>

      <StickyActions
        left={
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-xl border border-[rgb(var(--border))] bg-transparent px-4 py-2 text-sm font-medium text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--fg),0.05)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            >
              Cancelar
            </button>
            <p className="text-sm text-muted">Los campos clave quedan visibles arriba para evitar recorridos largos.</p>
          </div>
        }
        right={
          <button
            type="submit"
            disabled={isSubmitting}
            className="rounded-xl bg-[rgb(var(--brand))] px-6 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[rgba(var(--brand-700),0.9)] disabled:cursor-not-allowed disabled:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
          >
            {isSubmitting ? 'Guardando...' : isEdit ? 'Guardar cambios' : 'Crear proveedor'}
          </button>
        }
      />
    </form>
  );
}
